import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { SettingsState } from '../../organisms/SelectSettings';
import { Paper, Table, TableContainer, TableBody, TableRow, TableCell, TextField, TableHead } from '@material-ui/core';
import {
  GroupListType,
  FFT_CMP_GROUP_SET,
  DeviceSelectItem,
  GetFFTComparisonGroupList,
  PutFFTComparisonGroupList,
} from '../../../function/GroupDataModel';
import ItemComboBox, { ComboBoxItemType } from '../../atoms/ComboBox/ItemComboBox';
import DeviceMeasItem from './DeviceMeasItem';
import SaveCancel from './SaveCancel';
import { useModal } from '../../../function/useModal';
import ConfirmDialog, { ConfirmDialogResponse } from '../../atoms/Dialog/ConfirmDialog';
import MessageDialog, { MessageDialogResponse } from '../../atoms/Dialog/MessageDialog';
import { useTranslation } from 'react-i18next';
import CreateGroupDialog, { CreateGroupDialogResponse } from './CreateGroupDialog';
import { DefaultKindNo, GetVibrationSpecialStringByKindNo } from '../../../Constant';

type FFTComparisonSettingProps = {
  editData: SettingsState;
};

type SaveParameter = {
  list: FFT_CMP_GROUP_SET[];
};

const MaxGroup: number = 256;

const useStyles = makeStyles(
  createStyles({
    tableStyle: {
      width: '400px',
      borderStyle: 'none',
    },
    rowStyle: {
      borderStyle: 'none',
    },
    cellStyle: {
      borderStyle: 'none',
    },
    cellName: {
      width: '140px',
    },
    cellData: {
      width: '220px',
    },
    selectNo: {
      width: '120px',
    },
    selectName: {
      width: '200px',
    },
    dropdownStyle: {
      maxHeight: 300,
    },
    gridStyle: {
      borderStyle: 'solid',
      maxWidth: '500px',
    },
    tableHeader: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      backgroundColor: 'grey',
      textAlign: 'center',
      color: 'white',
      padding: '1px',
      minWidth: '80px',
      borderCollapse: `collapse`,
    },
  })
);

const FFTComparisonSetting: React.FC<FFTComparisonSettingProps> = (props: FFTComparisonSettingProps) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const [groupNo, setGroupNo] = useState<number>(0);
  const [editList, setEditList] = useState<FFT_CMP_GROUP_SET[]>([]);
  const [groupName, setGroupName] = useState<string>('');
  const [selectList, setSelectList] = useState<DeviceSelectItem[]>([]);
  const [groupCombos, setGroupCombos] = useState<ComboBoxItemType[]>();
  const [plcNo1, setPlcNo1] = useState<number>(0);
  const [setNo1, setSetNo1] = useState<number>(0);
  const [kind1, setKind1] = useState<number>(0);
  const [plcNo2, setPlcNo2] = useState<number>(0);
  const [setNo2, setSetNo2] = useState<number>(0);
  const [kind2, setKind2] = useState<number>(0);
  const [plcNo3, setPlcNo3] = useState<number>(0);
  const [setNo3, setSetNo3] = useState<number>(0);
  const [kind3, setKind3] = useState<number>(0);
  const [flgCfmDlg, actCfmDlg] = useModal<ConfirmDialogResponse>();
  const [flgMsgDlg, actMsgDlg] = useModal<MessageDialogResponse>();
  const [dlgmsg, setDlgmsg] = useState<string>('');
  const [flgCrtDlg, actCrtDlg] = useModal<CreateGroupDialogResponse>();

  const cfmdlg = flgCfmDlg ? (
    <ConfirmDialog
      dialog={actCfmDlg}
      message={dlgmsg}
      action={t('FFTComparisonSetting.はい', 'はい')}
      cancel={t('FFTComparisonSetting.いいえ', 'いいえ')}
    />
  ) : null;
  const msgdlg = flgMsgDlg ? <MessageDialog dialog={actMsgDlg} message={dlgmsg} /> : null;
  const crtdlg = flgCrtDlg ? (
    <CreateGroupDialog dialog={actCrtDlg} exists={groupCombos?.map<number>((gc) => gc.index)} maxGroup={MaxGroup} />
  ) : null;

  console.log('-- FFTComparisonSetting --');

  /**
   * 計測種別
   * 振動（速度）、振動（変位）のみ多言語切替
   */
  const changeLangMeasCombos = (items: DeviceSelectItem[]) => {
    let kindNo = -1;
    for (const [i, deviceSelectItem] of Object.entries(items)) {
      //console.debug(`この配列${i}は${deviceSelectItem.DEV_INDEX}-${deviceSelectItem.DEV_NO}です`);
      for (const [j, measureSelect] of Object.entries(deviceSelectItem.MeasureSelects)) {
        //console.debug(`この配列${j}は${measureSelect.MES_NAME}です`);
        for (const [k, itemSelect] of Object.entries(measureSelect.ItemSelects)) {
          //console.debug(`この配列${k}のKINDは${itemSelect.KIND}です`);
          if (itemSelect.KIND === DefaultKindNo.VIBRATION_SPD || itemSelect.KIND === DefaultKindNo.VIBRATION_DSP) {
            kindNo = itemSelect.KIND;
            break;
          }
        }
        if (kindNo === DefaultKindNo.VIBRATION_SPD || kindNo === DefaultKindNo.VIBRATION_DSP) {
          measureSelect.MES_NAME = GetVibrationSpecialStringByKindNo(kindNo, t);
          kindNo = -1;
        }
      }
    }

    //FFTで必要ない項目は省く
    items.forEach((r) => (r.MeasureSelects = r.MeasureSelects.filter((rr) => rr.FFT_INVALID === 0)));

    console.debug(['changeLangMeasCombos useEffect', items]);
    setSelectList([...items]);
  };

  // 言語切替
  useEffect(() => {
    console.debug(['FFTComparisonSetting useEffect i18n', i18n]);
    changeLangMeasCombos(selectList);
  }, [i18n.language]);

  useEffect(() => {
    //console.log('-- fetch FFTComparison settings --');
    GetFFTComparisonGroupList().then((result: GroupListType) => {
      console.debug(['FFTComparisonSetting useEffect result', result]);
      const groupList: FFT_CMP_GROUP_SET[] = result.FFT_CMP_GROUP_SETs.filter((fft) => fft.GROUP_NAME !== '');
      setEditList(groupList);
      //多言語設定
      changeLangMeasCombos(result.DeviceSelects);

      if (groupList.length > 0) {
        const grp = groupList[0];
        setGroupNo(grp.GROUP_NO);
        setGroupName(grp.GROUP_NAME);

        setPlcNo1(grp.PLC_NO1);
        setSetNo1(grp.SET_NO1);
        setKind1(grp.KIND1 ?? 0);
        setPlcNo2(grp.PLC_NO2);
        setSetNo2(grp.SET_NO2);
        setKind2(grp.KIND2 ?? 0);
        setPlcNo3(grp.PLC_NO3);
        setSetNo3(grp.SET_NO3);
        setKind3(grp.KIND3 ?? 0);
      }

      props.editData.SaveFunction = saveCurrentSetting;
      props.editData.IsUpdate = false;
    });
  }, [props.editData, props.editData.CancelCondition]);

  useEffect(() => {
    const groups = editList.map<ComboBoxItemType>((grp) => {
      return { index: grp.GROUP_NO, name: grp.GROUP_NO.toString() };
    });
    setGroupCombos(groups);

    if (!editList.find((el) => el.GROUP_NO == groupNo)) {
      if (editList.length > 0) {
        const grp = editList[0];
        setGroupNo(grp.GROUP_NO);
      }
    }
  }, [editList]);

  useEffect(() => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      setGroupName(grp.GROUP_NAME ?? '');

      if (selectList) {
        setPlcNo1(grp.PLC_NO1);
        setSetNo1(grp.SET_NO1);
        setKind1(grp.KIND1 ?? 0);
        setPlcNo2(grp.PLC_NO2);
        setSetNo2(grp.SET_NO2);
        setKind2(grp.KIND2 ?? 0);
        setPlcNo3(grp.PLC_NO3);
        setSetNo3(grp.SET_NO3);
        setKind3(grp.KIND3 ?? 0);
      }
    }
  }, [groupNo]);

  const groupChange = (no: number) => {
    updateDispFlg();
    setGroupNo(no);
  };
  const updateDispFlg = () => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      if (grp.GROUP_NAME === '' && grp.PLC_NO1 === 0 && grp.PLC_NO2 === 0 && grp.PLC_NO3 === 0) {
        grp.DSP_FLG = 0;
      } else {
        grp.DSP_FLG = 1;
      }
    }
    console.debug(['updateDispFlg', grp]);
  };
  const groupNameChange = (name: string) => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      setGroupName(name);
      grp.GROUP_NAME = name;

      props.editData.Target = { list: editList };
      props.editData.IsUpdate = true;
    }
  };
  const groupPlcSet1 = (plc: number, set: number, kind: number) => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      grp.PLC_NO1 = plc;
      grp.SET_NO1 = set;
      grp.KIND1 = kind;

      props.editData.Target = { list: editList };
      props.editData.IsUpdate = true;

      updateDispFlg();
      setPlcNo1(plc);
      setSetNo1(set);
      setKind1(kind);
    }
  };
  const groupPlcSet2 = (plc: number, set: number, kind: number) => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      grp.PLC_NO2 = plc;
      grp.SET_NO2 = set;
      grp.KIND2 = kind;

      props.editData.Target = { list: editList };
      props.editData.IsUpdate = true;

      updateDispFlg();
      setPlcNo2(plc);
      setSetNo2(set);
      setKind2(kind);
    }
  };
  const groupPlcSet3 = (plc: number, set: number, kind: number) => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      grp.PLC_NO3 = plc;
      grp.SET_NO3 = set;
      grp.KIND3 = kind;

      props.editData.Target = { list: editList };
      props.editData.IsUpdate = true;

      updateDispFlg();
      setPlcNo3(plc);
      setSetNo3(set);
      setKind3(kind);
    }
  };
  const saveCurrentSetting = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as SaveParameter;
      if (sp && sp.list) {
        const errmsg = validateSetting(sp.list);
        if (errmsg === '') {
          PutFFTComparisonGroupList(sp.list)
            .then((count: number) => {
              console.log(count);

              resolve('done');
            })
            .catch((reasone) => reject(reasone));
        } else {
          setDlgmsg(errmsg);
          actMsgDlg.open().apply((_) => setDlgmsg(''));
          reject('not save');
        }
      } else {
        reject('data nothing');
      }
    });
  };
  const validateSetting = (groups: FFT_CMP_GROUP_SET[]): string => {
    for (var idx = 0; idx < groups.length; idx++) {
      if (groups[idx].GROUP_NAME == '') {
        return t('FFTComparisonSetting.グループ名必須', 'グループ名は必須です');
      }
    }
    return '';
  };
  const deleteCurrentSetting = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as SaveParameter;
      if (sp && sp.list) {
        PutFFTComparisonGroupList(sp.list)
          .then((count: number) => {
            console.log(count);

            resolve('done');
          })
          .catch((reasone) => reject(reasone));
      } else {
        reject('data nothing');
      }
    });
  };

  //console.debug(['- 画面描画開始 -', selectList]);
  return (
    <div>
      <TableContainer>
        <Table className={classes.tableStyle}>
          <TableBody>
            <TableRow className={classes.rowStyle}>
              <TableCell className={classes.cellName + ' ' + classes.cellStyle}>
                {t('FFTComparisonSetting.グループNo', 'グループNo')}
              </TableCell>
              <TableCell className={classes.cellData + ' ' + classes.cellStyle}>
                <ItemComboBox
                  itemList={groupCombos}
                  label=""
                  value={groupNo}
                  onChangeEvent={(e) => {
                    if (typeof e.target.value === 'number') {
                      groupChange(e.target.value);
                    } else if (typeof e.target.value === 'string') {
                      groupChange(parseInt(e.target.value));
                    }
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow className={classes.rowStyle}>
              <TableCell className={classes.cellName + ' ' + classes.cellStyle}>
                {t('FFTComparisonSetting.グループ名称', 'グループ名称')}
              </TableCell>
              <TableCell className={classes.cellData + ' ' + classes.cellStyle}>
                <TextField
                  value={groupName}
                  inputProps={{maxLength:32}}
                  onChange={(e) => {
                    groupNameChange(e.target.value);
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table className={classes.gridStyle}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader} width={'180px'}>
                {t('FFTComparisonSetting.機器番号', '機器番号')}
              </TableCell>
              <TableCell className={classes.tableHeader} width={'200px'}>
                {t('FFTComparisonSetting.計測種別', '計測種別')}
              </TableCell>
              <TableCell className={classes.tableHeader} width={'200px'}>
                {t('FFTComparisonSetting.項目', '項目')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DeviceMeasItem
              selectList={selectList}
              plcNo={plcNo1}
              setNo={setNo1}
              kind={kind1}
              plcSetFunc={groupPlcSet1}
            />
            <DeviceMeasItem
              selectList={selectList}
              plcNo={plcNo2}
              setNo={setNo2}
              kind={kind2}
              plcSetFunc={groupPlcSet2}
            />
            <DeviceMeasItem
              selectList={selectList}
              plcNo={plcNo3}
              setNo={setNo3}
              kind={kind3}
              plcSetFunc={groupPlcSet3}
            />
          </TableBody>
        </Table>
        <SaveCancel
          saveAction={() => {
            updateDispFlg();
            setDlgmsg(t('FFTComparisonSetting.保存しますか?', '保存しますか?'));
            actCfmDlg
              .open()
              .apply((_) => {
                saveCurrentSetting({ list: editList }).then((_) => {
                  props.editData.IsUpdate = false;
                  // props.editData.CloseSetting();
                });
              })
              .cancel(() => {});
          }}
          cancelAction={() => {
            if (props.editData.IsUpdate) {
              setDlgmsg(
                t(
                  'FFTComparisonSetting.保存していないデータを破棄します。\nよろしいですか?',
                  '保存していないデータを破棄します。\nよろしいですか?'
                )
              );
              actCfmDlg
                .open()
                .apply((_) => {
                  // props.editData.IsUpdate = false;
                  props.editData.UpdateCancel();
                  // props.editData.CloseSetting();
                })
                .cancel(() => {});
            } else {
              // props.editData.CloseSetting();
            }
          }}
          createAction={() => {
            actCrtDlg.open().apply((info) => {
              const grp = editList.find((ed) => ed.GROUP_NO == info.no);
              if (!grp) {
                const newGrp: FFT_CMP_GROUP_SET = {
                  GROUP_NO: info.no,
                  GROUP_NAME: '',
                  DSP_FLG: 0,
                  PLC_NO1: 0,
                  SET_NO1: 0,
                  PLC_NO2: 0,
                  SET_NO2: 0,
                  PLC_NO3: 0,
                  SET_NO3: 0,
                  KIND1: 0,
                  KIND2: 0,
                  KIND3: 0,
                  Time_No1: '',
                  Time_No2: '',
                  Time_No3: '',
                };
                const newList = editList.concat(newGrp).sort((g1, g2) => g1.GROUP_NO - g2.GROUP_NO);
                setEditList(newList);
              }
              setGroupNo(info.no);
            });
          }}
          deleteAction={() => {
            setDlgmsg(t('FFTComparisonSetting.削除しますか?', '削除しますか?'));
            actCfmDlg.open().apply((_) => {
              const grp = editList.find((ed) => ed.GROUP_NO == groupNo);
              if (grp) {
                grp.GROUP_NAME = '';
                deleteCurrentSetting({ list: editList }).then((_) => {
                  props.editData.IsSaveClose = true;
                  props.editData.IsUpdate = false;
                  setEditList(editList.filter((ph) => ph.GROUP_NAME !== ''));
                });
              }
            });
          }}
        />
      </TableContainer>
      {cfmdlg}
      {msgdlg}
      {crtdlg}
    </div>
  );
};

export default FFTComparisonSetting;
